import { ReactNode } from 'react';

import Logo from '@/assets/nexlev-logo.svg';

interface IChildrenProps {
  children: ReactNode;
}

/**
 * Full-page shell for the standalone notice screens (deprecation, extension
 * login): soft blue backdrop, logo, a centred white card and a footer.
 */
const NoticeLayout = ({ children }: IChildrenProps) => (
  <div className="relative min-h-screen w-full overflow-hidden bg-[#F2F6FE] font-jakarta flex flex-col">
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{
        background:
          'radial-gradient(ellipse 45% 55% at 50% 45%, rgba(59, 130, 246, 0.16), transparent 70%)',
      }}
    />

    {/* Wide screens: the logo floats over the page so the card centres in
        the full viewport height. */}
    <header className="relative lg:absolute lg:inset-x-0 lg:top-0 z-10 px-6 pt-5 md:px-12">
      <img src={Logo} alt="NexLev" className="w-[8.25rem] h-[2.875rem]" />
    </header>

    <main className="relative flex-1 flex flex-col items-center justify-center gap-8 px-4 py-6">
      <div className="w-full max-w-[35rem] bg-white rounded-[1.75rem] shadow-[0_24px_64px_-20px_rgba(30,64,175,0.22)] px-6 py-8 sm:px-11 sm:py-9 flex flex-col items-center text-center">
        {children}
      </div>

      <p className="text-sm font-medium text-[#4B5B7A]">NexLev</p>
    </main>
  </div>
);

export const NoticeIconBadge = ({ children }: IChildrenProps) => (
  <div className="flex items-center justify-center w-[7.5rem] h-[7.5rem] rounded-full bg-[#E8F0FD] text-[#1D4FF0]">
    {children}
  </div>
);

export const NoticeEyebrow = ({ children }: IChildrenProps) => (
  <p className="mt-5 text-[0.8125rem] font-bold uppercase tracking-[0.25em] text-[#1D4FF0]">
    {children}
  </p>
);

export const NoticeHeading = ({ children }: IChildrenProps) => (
  <h1 className="mt-2.5 text-[2.25rem] sm:text-[2.75rem] font-extrabold leading-[1.08] tracking-[-0.015em] text-[#0B1220] [text-wrap:balance]">
    {children}
  </h1>
);

export const NoticeText = ({ children }: IChildrenProps) => (
  <p className="mt-4 max-w-[26rem] text-base sm:text-lg leading-relaxed text-[#4A5468]">
    {children}
  </p>
);

interface INoticeSpinnerProps {
  /** Sets both the diameter and the ring width. */
  sizeClassName?: string;
}

export const NoticeSpinner = ({
  sizeClassName = 'w-5 h-5 border-2',
}: INoticeSpinnerProps) => (
  <span
    aria-hidden="true"
    className={`${sizeClassName} shrink-0 rounded-full border-[#D3E0FB] border-t-[#1D4FF0] animate-spin motion-reduce:animate-none`}
  />
);

export const NoticeArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5 shrink-0"
    aria-hidden="true"
  >
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

/** Spinner + live text, announced to screen readers as it changes. */
export const NoticeStatus = ({ children }: IChildrenProps) => (
  <div
    role="status"
    aria-live="polite"
    className="mt-5 flex items-center gap-3 text-[0.9375rem] text-[#4A5468]"
  >
    <NoticeSpinner />
    <span>{children}</span>
  </div>
);

export const NoticeFootnote = ({ children }: IChildrenProps) => (
  <div className="mt-6 w-full border-t border-[#E3E8F2] pt-5 text-sm leading-relaxed text-[#5B6578]">
    {children}
  </div>
);

export default NoticeLayout;
